/* ==========================================================================
   OTX INFLUENCER FORM -- product style autocomplete

   Upgrades the "Requested Item" text input on the creator page into a
   type-ahead that searches the live catalogue. Free text is still allowed on
   purpose: an applicant may want something not currently listed, and blocking
   that would lose applications.

   Data source is Shopify's own predictive search endpoint,
   /search/suggest.json -- no product list is baked into the template, so it
   stays correct as the catalogue changes and costs nothing on page load.

   Loaded by a `custom-liquid` SECTION on templates/page.creator.json rather
   than by editing sections/become-influencer.liquid, which is 41 KB and would
   have to be re-sent in full for a one-line change.

   STACKING NOTE (see CSS below): become-influencer.liquid sets
   `#gu-inf-<id> .gu-form > * { position: relative; z-index: 1; }`. That gives
   the field grid AND the submit button the same z-index, so the button --
   being later in the DOM -- painted over the suggestions dropdown. The grid is
   lifted above it here. `.gu-form` also carries `overflow:hidden`, which would
   clip the dropdown once the field sits near the bottom of the panel, so that
   is released too. Both need !important to beat the section's ID-scoped rules.
   ========================================================================== */
(function () {
  'use strict';

  var FIELD_KEY = 'Requested Item';
  var STYLE_ID = 'otx-ac-style';
  var MIN_CHARS = 2;

  var CSS = [
    /* --- stacking fixes, see header comment --- */
    '.gu-form { overflow: visible !important; }',
    '.gu-form > .gu-formgrid { position: relative !important; z-index: 5 !important; }',
    '.gu-form > .gu-submit { position: relative; z-index: 1; }',

    '.otx-ac { position: relative; z-index: 5; }',
    '.otx-ac__list { position: absolute; z-index: 60; left: 0; right: 0; top: calc(100% + 4px);',
    '  max-height: 320px; overflow-y: auto; background: #0d0d0d;',
    '  border: 1px solid rgba(255,255,255,.22); border-top: 2px solid #d21c1c;',
    '  box-shadow: 0 18px 40px rgba(0,0,0,.75); display: none; }',
    '.otx-ac--open .otx-ac__list { display: block; }',
    '.otx-ac__item { display: flex; align-items: center; gap: 12px; padding: 10px 12px;',
    '  cursor: pointer; border-bottom: 1px solid rgba(255,255,255,.07); }',
    '.otx-ac__item:last-child { border-bottom: 0; }',
    '.otx-ac__item[aria-selected="true"], .otx-ac__item:hover { background: rgba(210,28,28,.16); }',
    '.otx-ac__thumb { width: 38px; height: 38px; flex: none; object-fit: cover;',
    '  background: #191919; }',
    '.otx-ac__title { font-size: 14px; line-height: 1.3; color: #ffffff; }',
    '.otx-ac__note { padding: 10px 12px; font-size: 12px; color: rgba(255,255,255,.5); }'
  ].join('\n');

  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;
    var el = document.createElement('style');
    el.id = STYLE_ID;
    el.textContent = CSS;
    document.head.appendChild(el);
  }

  function build(input) {
    if (input.dataset.otxAc) return;
    input.dataset.otxAc = '1';
    input.setAttribute('autocomplete', 'off');
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-autocomplete', 'list');

    var wrap = document.createElement('div');
    wrap.className = 'otx-ac';
    input.parentNode.insertBefore(wrap, input);
    wrap.appendChild(input);

    var list = document.createElement('div');
    list.className = 'otx-ac__list';
    list.setAttribute('role', 'listbox');
    wrap.appendChild(list);

    var items = [];
    var active = -1;
    var timer = null;
    var lastQuery = '';

    function close() {
      wrap.classList.remove('otx-ac--open');
      input.setAttribute('aria-expanded', 'false');
      active = -1;
    }

    function open() {
      wrap.classList.add('otx-ac--open');
      input.setAttribute('aria-expanded', 'true');
    }

    function highlight(i) {
      items.forEach(function (el, n) {
        el.setAttribute('aria-selected', n === i ? 'true' : 'false');
      });
      active = i;
      if (items[i]) items[i].scrollIntoView({ block: 'nearest' });
    }

    function choose(title) {
      input.value = title;
      close();
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    function render(products) {
      list.innerHTML = '';
      items = [];
      if (!products.length) {
        var note = document.createElement('div');
        note.className = 'otx-ac__note';
        note.textContent = 'No match - you can still type it in yourself.';
        list.appendChild(note);
        open();
        return;
      }
      products.forEach(function (p) {
        var row = document.createElement('div');
        row.className = 'otx-ac__item';
        row.setAttribute('role', 'option');
        row.setAttribute('aria-selected', 'false');
        if (p.image) {
          var img = document.createElement('img');
          img.className = 'otx-ac__thumb';
          img.src = p.image;
          img.alt = '';
          img.loading = 'lazy';
          row.appendChild(img);
        }
        var t = document.createElement('span');
        t.className = 'otx-ac__title';
        t.textContent = p.title;
        row.appendChild(t);
        row.addEventListener('mousedown', function (e) {
          e.preventDefault();       /* beat the blur so the click registers */
          choose(p.title);
        });
        list.appendChild(row);
        items.push(row);
      });
      open();
    }

    function search(q) {
      var url = '/search/suggest.json?q=' + encodeURIComponent(q) +
                '&resources[type]=product&resources[limit]=8' +
                '&resources[options][fields]=title,product_type,variants.title';
      fetch(url, { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (q !== lastQuery) return;   /* a newer keystroke already won */
          var products = ((data.resources || {}).results || {}).products || [];
          render(products.map(function (p) {
            return { title: p.title, image: p.featured_image ? p.featured_image.url : p.image };
          }));
        })
        .catch(function () { close(); });
    }

    input.addEventListener('input', function () {
      var q = input.value.trim();
      lastQuery = q;
      window.clearTimeout(timer);
      if (q.length < MIN_CHARS) { close(); return; }
      timer = window.setTimeout(function () { search(q); }, 180);
    });

    input.addEventListener('keydown', function (e) {
      if (!wrap.classList.contains('otx-ac--open')) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); highlight(Math.min(active + 1, items.length - 1)); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); highlight(Math.max(active - 1, 0)); }
      else if (e.key === 'Enter' && active > -1) {
        e.preventDefault();                       /* don't submit the form */
        items[active].dispatchEvent(new Event('mousedown'));
      } else if (e.key === 'Escape') { close(); }
    });

    input.addEventListener('blur', function () { window.setTimeout(close, 120); });
  }

  function init() {
    injectStyles();
    var sel = 'input[name="contact[' + FIELD_KEY + ']"]';
    var list = document.querySelectorAll(sel);
    for (var i = 0; i < list.length; i++) build(list[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* the theme editor re-renders the section on every setting change */
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', init);
  }
})();
